import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { formatCurrency } from '../../utils/roadmapCalcs';
import type { Stage } from '../../data/roadmapContent';
interface RevealScreenProps {
  step: number;
  total: number;
  stage: Stage;
  eyebrow: string;
  headlineValue: number;
  /** Optional secondary figure shown beneath the headline number. */
  subValue?: {
    label: string;
    value: number;
  };
  breakdown: {
    label: string;
    value: number;
    estimated?: boolean;
  }[];
  progress?: number;
  note?: string;
  onBack: () => void;
  onNext: () => void;
}

/**
 * A payoff screen partway through the flow. Long questionnaires that only pay
 * out at the very end lose people; these two land real value mid-conversation.
 */
export function RevealScreen({
  step,
  total,
  stage,
  eyebrow,
  headlineValue,
  subValue,
  breakdown,
  progress,
  note,
  onBack,
  onNext
}: RevealScreenProps) {
  return <div className="w-full" data-id="element-241" data-mp="35hjgf429dvlk">
      <ProgressBar step={step} total={total} stage={stage} data-id="element-242" data-mp="35h9rljsy9uhg" />

      <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-xl shadow-slate-900/5 dark:shadow-none p-6 sm:p-8" data-id="element-243" data-mp="3agmcw046athx">
        <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2" data-id="element-244" data-mp="2vitxr92jg749">
          {eyebrow}
        </p>
        <p className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white tabular-nums" data-id="element-245" data-mp="30kjbiwh5ayw1">
          {formatCurrency(headlineValue)}
        </p>

        {subValue && <p className="mt-2 text-base text-slate-600 dark:text-slate-300" data-id="element-246" data-mp="2rjuya3zkt2k7">
            {subValue.label}{' '}
            <span className="font-semibold text-slate-900 dark:text-white tabular-nums" data-id="element-247" data-mp="2cr18npqlb61t">
              {formatCurrency(subValue.value)}
            </span>
          </p>}

        {typeof progress === 'number' && <div className="mt-6" data-id="element-248" data-mp="27uarez31u4et">
            <div className="flex items-baseline justify-between mb-2" data-id="element-249" data-mp="27shkrjsfrk6t">
              <span className="text-sm text-slate-500 dark:text-slate-400" data-id="element-250" data-mp="ebbtg9kc69z6">
                Already covered
              </span>
              <span className="text-sm font-semibold text-primary-600 dark:text-primary-400" data-id="element-251" data-mp="9ir2dfsteazc">
                {Math.round(progress * 100)}%
              </span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden" data-id="element-252" data-mp="3vsbkmgumy21k">
              <div className="h-full rounded-full bg-gradient-to-r from-accent-500 to-primary-500 transition-[width] duration-300 ease-out" style={{
            width: `${Math.round(progress * 100)}%`
          }} data-id="element-253" data-mp="x8v57ky6i6pa" />
            </div>
          </div>}

        <dl className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10 space-y-3" data-id="element-254" data-mp="j9deh01bojda">
          {breakdown.map(row => <div key={row.label} className="flex items-baseline justify-between gap-4" data-id="element-255" data-mp="37zmm64fkgmmb">
              <dt className="text-sm text-slate-600 dark:text-slate-400" data-id="element-256" data-mp="342gtcen7w2a9">
                {row.label}
                {row.estimated && <span className="ml-1.5 text-xs text-slate-400 dark:text-slate-500" data-id="element-257" data-mp="1njf2dmh46220">
                    (estimate)
                  </span>}
              </dt>
              <dd className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums" data-id="element-258" data-mp="1x9q6pa4pmd9f">
                {formatCurrency(row.value)}
              </dd>
            </div>)}
        </dl>
      </div>

      {note && <p className="mt-5 text-base text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-259" data-mp="2774mr8mq1017">
          {note}
        </p>}

      <div className="mt-8 flex items-center gap-3" data-id="element-260" data-mp="2ba10om1bg7yt">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 px-5 py-4 rounded-xl border border-slate-200 dark:border-white/10 text-base font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" data-id="element-261" data-mp="2g8cch2b68sjo">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-262" data-mp="3mozlmhssz253" />
          Back
        </button>
        <button type="button" onClick={onNext} className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white text-lg font-semibold transition-[transform,box-shadow] duration-200 ease-out hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] hover:scale-[1.01] group" data-id="element-263" data-mp="3hqy2xxzah4u5">
          Keep going
          <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-264" data-mp="2z0gei5lhovel" />
        </button>
      </div>
    </div>;
}